import { z } from 'zod';

import type { TFunction } from 'i18next';
import type { ComposeMode } from '../types';
import {
  OUTBOUND_ATTACHMENT_MAX_BYTES,
  OUTBOUND_ATTACHMENT_MAX_COUNT,
  OUTBOUND_CC_MAX_COUNT,
  OUTBOUND_SUBJECT_MAX_LENGTH,
  OUTBOUND_TO_MAX_COUNT,
} from '@minty/shared/email';
import type { UploadedAttachment } from '~/components/message-composer/upload-attachments';

export type ComposerFormValues = {
  to: Array<string>;
  cc: Array<string>;
  subject: string;
  attachments: Array<File>;
};

// Replies take no recipients or subject: the server addresses them from the
// message they answer.
export function createComposerSchema(t: TFunction, mode: ComposeMode) {
  const addressed = mode === 'new' || mode === 'forward';

  return z
    .object({
      to: addressed
        ? z
            .array(
              z.email({
                message: t('Enter a valid email address.', {
                  ns: 'mailbox',
                }),
              }),
            )
            .min(1, t('Add at least one recipient.', { ns: 'mailbox' }))
            .max(
              OUTBOUND_TO_MAX_COUNT,
              t('Too many recipients.', { ns: 'mailbox' }),
            )
        : z.array(z.string()),
      cc: (!addressed
        ? z.array(z.string())
        : z.array(
            z.email({
              message: t('Enter a valid email address.', { ns: 'mailbox' }),
            }),
          )
      ).max(
        OUTBOUND_CC_MAX_COUNT,
        t('Too many CC recipients.', { ns: 'mailbox' }),
      ),
      subject:
        mode === 'new'
          ? z
              .string()
              .trim()
              .min(1, t('Add a subject.', { ns: 'mailbox' }))
              .max(
                OUTBOUND_SUBJECT_MAX_LENGTH,
                t('Subject must be {{count}} characters or fewer.', {
                  count: OUTBOUND_SUBJECT_MAX_LENGTH,
                  ns: 'mailbox',
                }),
              )
          : z.string(),
      attachments: z.array(z.instanceof(File)).superRefine((files, ctx) => {
        if (files.some((file) => file.size === 0)) {
          ctx.addIssue({
            code: 'custom',
            message: t('Attachments cannot be empty.', { ns: 'mailbox' }),
          });
        }

        if (files.some((file) => file.name.trim().length === 0)) {
          ctx.addIssue({
            code: 'custom',
            message: t('Attachment names cannot be empty.', {
              ns: 'mailbox',
            }),
          });
        }

        if (files.some((file) => file.size > OUTBOUND_ATTACHMENT_MAX_BYTES)) {
          ctx.addIssue({
            code: 'custom',
            message: t('Each attachment must be {{size}} MB or smaller.', {
              size: OUTBOUND_ATTACHMENT_MAX_BYTES / 1024 / 1024,
              ns: 'mailbox',
            }),
          });
        }
      }),
    })
    .superRefine((values, ctx) => {
      const sizes = values.attachments.map((file) => file.size);

      if (sizes.length > OUTBOUND_ATTACHMENT_MAX_COUNT) {
        ctx.addIssue({
          code: 'custom',
          path: ['attachments'],
          message: t('You can attach up to {{count}} files.', {
            count: OUTBOUND_ATTACHMENT_MAX_COUNT,
            ns: 'mailbox',
          }),
        });
      }

      if (
        sizes.reduce((total, size) => total + size, 0) >
        OUTBOUND_ATTACHMENT_MAX_BYTES
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['attachments'],
          message: t('Attachments must be {{size}} MB or smaller in total.', {
            size: OUTBOUND_ATTACHMENT_MAX_BYTES / 1024 / 1024,
            ns: 'mailbox',
          }),
        });
      }
    });
}

/** Parallel by construction: `storageIds` is what uploading these files
 *  produced, in the same order. */
export function toOutgoingAttachments(
  files: Array<File>,
  uploads: Array<UploadedAttachment>,
) {
  return uploads.flatMap(({ fileId: storageId, receipt }, index) => {
    const file = files[index];

    return file
      ? [
          {
            storageId,
            receipt,
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
          },
        ]
      : [];
  });
}
